import React, { useEffect, useState } from 'react';
import { Plus, Edit, Trash2, Users, AlertTriangle, X } from 'lucide-react';
import { incidentApi, employeeApi } from '../services/api';
import { Incident, CreateIncidentRequest, Employee, InjuredPerson, InjuryDetail, GardaInvolved } from '../types';

const emptyInjuredPerson: InjuredPerson = {
  name: '',
  address: '',
  phone: '',
  birthDate: '',
};

const emptyInjuryDetail: InjuryDetail = {
  type: '',
  firstAid: '',
  hospitalVisitBy: '',
  helpedBy: '',
};

const emptyGarda: GardaInvolved = {
  name: '',
  gardaId: '',
  pulseId: '',
};

const IncidentAdmin: React.FC = () => {
  const [incidents, setIncidents] = useState<Incident[]>([]);
  const [employees, setEmployees] = useState<Employee[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [editingIncident, setEditingIncident] = useState<Incident | null>(null);
  const [formData, setFormData] = useState<CreateIncidentRequest>({
    title: '',
    description: '',
    venue: '',
    location: '',
    dateTime: '',
    type: '',
    cctvCaught: false,
    cctvBurned: false,
    gardaPresent: false,
    photosTaken: false,
    photosAltered: false,
    slipTripFall: 'N/A',
    footwear: '',
    spillage: '',
    spillageDuration: '',
    securityOnDuty: '',
    managerOnDuty: '',
    status: 'Open',
    othersOnDuty: '',
    reportedByEmployeeId: 0,
    injuredPersons: [emptyInjuredPerson],
    injuryDetails: [emptyInjuryDetail],
    gardaInvolved: [],
  });

  useEffect(() => {
    const load = async () => {
      try {
        setLoading(true);
        const [inc, emps] = await Promise.all([incidentApi.getAll(), employeeApi.getAll()]);
        setIncidents(inc);
        setEmployees(emps);
      } catch (error) {
        console.error('Error loading incidents:', error);
        alert('Failed to load incidents. Please try again.');
      } finally {
        setLoading(false);
      }
    };

    load();
  }, []);

  const resetForm = () => {
    setEditingIncident(null);
    setFormData({
      title: '',
      description: '',
      venue: '',
      location: '',
      dateTime: '',
      type: '',
      cctvCaught: false,
      cctvBurned: false,
      gardaPresent: false,
      photosTaken: false,
      photosAltered: false,
      slipTripFall: '',
      footwear: '',
      spillage: '',
      spillageDuration: '',
      securityOnDuty: '',
      managerOnDuty: '',
      status: 'Open',
      othersOnDuty: '',
      reportedByEmployeeId: 0,
      injuredPersons: [emptyInjuredPerson],
      injuryDetails: [emptyInjuryDetail],
      gardaInvolved: [],
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (saving) return;

    if (!formData.title.trim()) {
      alert('Title is required.');
      return;
    }
    if (formData.reportedByEmployeeId === 0) {
      alert('Please select Reported By.');
      return;
    }
    if (!formData.injuredPersons.length || !formData.injuredPersons[0].name.trim()) {
      alert('Please add at least one injured person.');
      return;
    }
    if (!formData.injuryDetails.length || !formData.injuryDetails[0].type?.trim()) {
      alert('Please add at least one injury detail.');
      return;
    }

    try {
      setSaving(true);
      if (editingIncident) {
        await incidentApi.update(editingIncident.id, formData);
      } else {
        await incidentApi.create(formData);
      }

      const updated = await incidentApi.getAll();
      setIncidents(updated);
      resetForm();
      setShowForm(false);
    } catch (error: any) {
      console.error('Error saving incident:', error);
      const message = error?.response?.data || error?.message || 'Failed to save incident.';
      alert(message);
    } finally {
      setSaving(false);
    }
  };

  const startEdit = (incident: Incident) => {
    setEditingIncident(incident);
    setFormData({
      title: incident.title,
      description: incident.description || '',
      venue: incident.venue || '',
      location: incident.location || '',
      dateTime: incident.dateTime ? incident.dateTime.substring(0, 16) : '',
      type: incident.type || '',
      cctvCaught: !!incident.cctvCaught,
      cctvBurned: !!incident.cctvBurned,
      gardaPresent: !!incident.gardaPresent,
      photosTaken: !!incident.photosTaken,
      photosAltered: !!incident.photosAltered,
      slipTripFall: incident.slipTripFall || 'N/A',
      footwear: incident.footwear || '',
      spillage: incident.spillage || '',
      spillageDuration: incident.spillageDuration || '',
      securityOnDuty: incident.securityOnDuty || '',
      managerOnDuty: incident.managerOnDuty || '',
      status: incident.status || 'Open',
      othersOnDuty: incident.othersOnDuty || '',
      reportedByEmployeeId: incident.reportedByEmployeeId,
      injuredPersons: incident.injuredPersons.length
        ? incident.injuredPersons.map(p => ({
            name: p.name,
            address: p.address || '',
            phone: p.phone || '',
            birthDate: p.birthDate || '',
          }))
        : [emptyInjuredPerson],
      injuryDetails: incident.injuryDetails.length
        ? incident.injuryDetails.map(d => ({
            type: d.type,
            firstAid: d.firstAid || '',
            hospitalVisitBy: d.hospitalVisitBy || '',
            helpedBy: d.helpedBy || '',
          }))
        : [emptyInjuryDetail],
      gardaInvolved: incident.gardaInvolved.length
        ? incident.gardaInvolved.map(g => ({
            name: g.name,
            gardaId: g.gardaId,
            pulseId: g.pulseId,
          }))
        : [],
    });
    setShowForm(true);
  };

  const handleDelete = async (id: number) => {
    if (!window.confirm('Are you sure you want to delete this incident?')) return;
    try {
      await incidentApi.delete(id);
      const updated = await incidentApi.getAll();
      setIncidents(updated);
    } catch (error: any) {
      console.error('Error deleting incident:', error);
      const message = error?.response?.data || error?.message || 'Failed to delete incident.';
      alert(message);
    }
  };

  const updateInjuredPerson = (index: number, field: keyof InjuredPerson, value: string) => {
    setFormData(prev => {
      const list = [...prev.injuredPersons];
      list[index] = { ...list[index], [field]: value };
      return { ...prev, injuredPersons: list };
    });
  };

  const addInjuredPerson = () => {
    setFormData(prev => ({
      ...prev,
      injuredPersons: [...prev.injuredPersons, { ...emptyInjuredPerson }],
    }));
  };

  const removeInjuredPerson = (index: number) => {
    setFormData(prev => {
      const list = prev.injuredPersons.filter((_, i) => i !== index);
      return { ...prev, injuredPersons: list.length ? list : [{ ...emptyInjuredPerson }] };
    });
  };

  const updateInjuryDetail = (index: number, field: keyof InjuryDetail, value: string) => {
    setFormData(prev => {
      const list = [...prev.injuryDetails];
      list[index] = { ...list[index], [field]: value };
      return { ...prev, injuryDetails: list };
    });
  };

  const addInjuryDetail = () => {
    setFormData(prev => ({
      ...prev,
      injuryDetails: [...prev.injuryDetails, { ...emptyInjuryDetail }],
    }));
  };

  const removeInjuryDetail = (index: number) => {
    setFormData(prev => {
      const list = prev.injuryDetails.filter((_, i) => i !== index);
      return { ...prev, injuryDetails: list.length ? list : [{ ...emptyInjuryDetail }] };
    });
  };

  const updateGarda = (index: number, field: keyof GardaInvolved, value: string) => {
    setFormData(prev => {
      const list = [...prev.gardaInvolved];
      list[index] = { ...list[index], [field]: value };
      return { ...prev, gardaInvolved: list };
    });
  };

  const addGarda = () => {
    setFormData(prev => ({
      ...prev,
      gardaInvolved: [...prev.gardaInvolved, { ...emptyGarda }],
    }));
  };

  const removeGarda = (index: number) => {
    setFormData(prev => ({
      ...prev,
      gardaInvolved: prev.gardaInvolved.filter((_, i) => i !== index),
    }));
  };

  if (loading) {
    return (
      <div className="p-6">
        <div className="flex items-center justify-center h-64">
          <div className="text-neutral-500">Loading incidents...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      <div className="flex justify-between items-center">
        <h1 className="text-2xl font-bold text-neutral-800">Incident Admin</h1>
        <button
          onClick={() => {
            resetForm();
            setShowForm(true);
          }}
          className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
        >
          <Plus className="h-4 w-4" />
          Add Incident
        </button>
      </div>

      {/* List view */}
      <div className="bg-white rounded-lg shadow-sm border border-neutral-200">
        <div className="p-4 border-b border-neutral-200 flex items-center justify-between">
          <h2 className="text-lg font-semibold text-neutral-800">All Incidents</h2>
          <div className="flex items-center gap-2 text-sm text-neutral-500">
            <Users className="h-4 w-4" />
            <span>{incidents.length} total</span>
          </div>
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-neutral-200">
            <thead className="bg-neutral-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-neutral-500 uppercase tracking-wider">Title</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-neutral-500 uppercase tracking-wider">Date/Time</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-neutral-500 uppercase tracking-wider">Venue</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-neutral-500 uppercase tracking-wider">Location</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-neutral-500 uppercase tracking-wider">Status</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-neutral-500 uppercase tracking-wider">Reported By</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-neutral-500 uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-neutral-200">
              {incidents.length === 0 ? (
                <tr>
                  <td colSpan={7} className="px-4 py-6 text-center text-neutral-500">
                    No incidents found.
                  </td>
                </tr>
              ) : (
                incidents.map(incident => (
                  <tr key={incident.id}>
                    <td className="px-4 py-3 text-sm text-neutral-900">{incident.title}</td>
                    <td className="px-4 py-3 text-sm text-neutral-700">
                      {incident.dateTime ? new Date(incident.dateTime).toLocaleString() : 'N/A'}
                    </td>
                    <td className="px-4 py-3 text-sm text-neutral-700">{incident.venue || 'N/A'}</td>
                    <td className="px-4 py-3 text-sm text-neutral-700">{incident.location || 'N/A'}</td>
                    <td className="px-4 py-3 text-sm">
                      <span
                        className={`inline-flex items-center px-2 py-1 text-xs font-medium rounded-full ${
                          (incident.status || '').toLowerCase() === 'closed'
                            ? 'bg-green-50 text-green-700 border border-green-200'
                            : 'bg-orange-50 text-orange-700 border border-orange-200'
                        }`}
                      >
                        <AlertTriangle className="h-3 w-3 mr-1" />
                        {incident.status || 'Open'}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-sm text-neutral-700">
                      {incident.reportedByEmployee
                        ? `${incident.reportedByEmployee.firstName} ${incident.reportedByEmployee.lastName}`
                        : 'N/A'}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm font-medium">
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => startEdit(incident)}
                          className="text-blue-600 hover:text-blue-900"
                          title="Edit"
                        >
                          <Edit className="h-4 w-4" />
                        </button>
                        <button
                          onClick={() => handleDelete(incident.id)}
                          className="text-red-600 hover:text-red-900"
                          title="Delete"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Add/Edit form modal */}
      {showForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 overflow-y-auto">
          <div className="bg-white rounded-lg w-full max-w-5xl my-8 max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center border-b border-neutral-200 px-6 py-4">
              <h2 className="text-lg font-semibold text-neutral-800">
                {editingIncident ? 'Edit Incident' : 'Add New Incident'}
              </h2>
              <button
                onClick={() => {
                  setShowForm(false);
                  resetForm();
                }}
                className="text-neutral-400 hover:text-neutral-600"
                type="button"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            <form onSubmit={handleSubmit} className="space-y-4 p-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-neutral-700 mb-1">Title *</label>
                <input
                  type="text"
                  value={formData.title}
                  onChange={e => setFormData(prev => ({ ...prev, title: e.target.value }))}
                  className="w-full px-3 py-2 border border-neutral-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-neutral-700 mb-1">Reported By *</label>
                <select
                  value={formData.reportedByEmployeeId}
                  onChange={e =>
                    setFormData(prev => ({ ...prev, reportedByEmployeeId: parseInt(e.target.value, 10) || 0 }))
                  }
                  className="w-full px-3 py-2 border border-neutral-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value={0}>Select employee</option>
                  {employees.map(emp => (
                    <option key={emp.id} value={emp.id}>
                      {emp.firstName} {emp.lastName}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-neutral-700 mb-1">Venue</label>
                <input
                  type="text"
                  value={formData.venue || ''}
                  onChange={e => setFormData(prev => ({ ...prev, venue: e.target.value }))}
                  className="w-full px-3 py-2 border border-neutral-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-neutral-700 mb-1">Location</label>
                <input
                  type="text"
                  value={formData.location || ''}
                  onChange={e => setFormData(prev => ({ ...prev, location: e.target.value }))}
                  className="w-full px-3 py-2 border border-neutral-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-neutral-700 mb-1">Date &amp; Time</label>
                <input
                  type="datetime-local"
                  value={formData.dateTime || ''}
                  onChange={e => setFormData(prev => ({ ...prev, dateTime: e.target.value }))}
                  className="w-full px-3 py-2 border border-neutral-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-neutral-700 mb-1">Type</label>
                <select
                  value={formData.type || ''}
                  onChange={e => setFormData(prev => ({ ...prev, type: e.target.value }))}
                  className="w-full px-3 py-2 border border-neutral-300 rounded-md bg-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">Select type</option>
                  <option value="Accidental Injury">Accidental Injury</option>
                  <option value="Violence/Physical Assault">Violence/Physical Assault</option>
                  <option value="Verbal Aggression">Verbal Aggression</option>
                  <option value="Illegal Drugs/Controlled Substance">Illegal Drugs/Controlled Substance</option>
                  <option value="Fire">Fire</option>
                  <option value="Skin Puncture/Incision/Crush/Laceration/Abrasion">Skin Puncture/Incision/Crush/Laceration/Abrasion</option>
                  <option value="Slip/Trip/Fall">Slip/Trip/Fall</option>
                  <option value="Occupational">Occupational</option>
                  <option value="Other">Other</option>
                </select>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-neutral-700 mb-1">Description</label>
              <textarea
                rows={3}
                value={formData.description || ''}
                onChange={e => setFormData(prev => ({ ...prev, description: e.target.value }))}
                className="w-full px-3 py-2 border border-neutral-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>

            {/* Investigations Section */}
            <div className="border border-neutral-200 rounded-lg p-4 space-y-3">
              <h3 className="text-sm font-semibold text-neutral-800 mb-3">Investigations</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    id="cctvCaught"
                    checked={formData.cctvCaught || false}
                    onChange={e => setFormData(prev => ({ ...prev, cctvCaught: e.target.checked }))}
                    className="w-4 h-4 text-blue-600 border-neutral-300 rounded focus:ring-blue-500"
                  />
                  <label htmlFor="cctvCaught" className="text-sm text-neutral-700 cursor-pointer">
                    CCTV Caught
                  </label>
                </div>
                <div className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    id="gardaPresent"
                    checked={formData.gardaPresent || false}
                    onChange={e => setFormData(prev => ({ ...prev, gardaPresent: e.target.checked }))}
                    className="w-4 h-4 text-blue-600 border-neutral-300 rounded focus:ring-blue-500"
                  />
                  <label htmlFor="gardaPresent" className="text-sm text-neutral-700 cursor-pointer">
                    Garda Present
                  </label>
                </div>
                <div className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    id="photosAltered"
                    checked={formData.photosAltered || false}
                    onChange={e => setFormData(prev => ({ ...prev, photosAltered: e.target.checked }))}
                    className="w-4 h-4 text-blue-600 border-neutral-300 rounded focus:ring-blue-500"
                  />
                  <label htmlFor="photosAltered" className="text-sm text-neutral-700 cursor-pointer">
                    Photos Altered
                  </label>
                </div>
                <div className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    id="cctvBurned"
                    checked={formData.cctvBurned || false}
                    onChange={e => setFormData(prev => ({ ...prev, cctvBurned: e.target.checked }))}
                    className="w-4 h-4 text-blue-600 border-neutral-300 rounded focus:ring-blue-500"
                  />
                  <label htmlFor="cctvBurned" className="text-sm text-neutral-700 cursor-pointer">
                    CCTV Burned
                  </label>
                </div>
                <div className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    id="photosTaken"
                    checked={formData.photosTaken || false}
                    onChange={e => setFormData(prev => ({ ...prev, photosTaken: e.target.checked }))}
                    className="w-4 h-4 text-blue-600 border-neutral-300 rounded focus:ring-blue-500"
                  />
                  <label htmlFor="photosTaken" className="text-sm text-neutral-700 cursor-pointer">
                    Photos Taken
                  </label>
                </div>
              </div>
            </div>

            {/* Slip/Trip/Fall Information Section */}
            <div className="border border-neutral-200 rounded-lg p-4 space-y-3">
              <h3 className="text-sm font-semibold text-neutral-800 mb-3">Slip/Trip/Fall Information</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-neutral-700 mb-1">Slip/Trip/Fall</label>
                  <select
                    value={formData.slipTripFall || ''}
                    onChange={e => setFormData(prev => ({ ...prev, slipTripFall: e.target.value }))}
                    className="w-full px-3 py-2 border border-neutral-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="">Select...</option>
                    <option value="Slip">Slip</option>
                    <option value="Trip">Trip</option>
                    <option value="Fall">Fall</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-neutral-700 mb-1">Footwear</label>
                  <input
                    type="text"
                    value={formData.footwear || ''}
                    onChange={e => setFormData(prev => ({ ...prev, footwear: e.target.value }))}
                    placeholder="Describe footwear"
                    className="w-full px-3 py-2 border border-neutral-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-neutral-700 mb-1">Spillage</label>
                  <input
                    type="text"
                    value={formData.spillage || ''}
                    onChange={e => setFormData(prev => ({ ...prev, spillage: e.target.value }))}
                    placeholder="Describe spillage"
                    className="w-full px-3 py-2 border border-neutral-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-neutral-700 mb-1">Spillage Duration</label>
                  <input
                    type="text"
                    value={formData.spillageDuration || ''}
                    onChange={e => setFormData(prev => ({ ...prev, spillageDuration: e.target.value }))}
                    placeholder="How long was spillage present"
                    className="w-full px-3 py-2 border border-neutral-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
              </div>
            </div>

            {/* Personnel Information Section */}
            <div className="border border-neutral-200 rounded-lg p-4 space-y-3">
              <h3 className="text-sm font-semibold text-neutral-800 mb-3">Personnel Information</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-neutral-700 mb-1">Security On Duty</label>
                  <input
                    type="text"
                    value={formData.securityOnDuty || ''}
                    onChange={e => setFormData(prev => ({ ...prev, securityOnDuty: e.target.value }))}
                    placeholder="Security staff on duty"
                    className="w-full px-3 py-2 border border-neutral-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-neutral-700 mb-1">Manager On Duty</label>
                  <input
                    type="text"
                    value={formData.managerOnDuty || ''}
                    onChange={e => setFormData(prev => ({ ...prev, managerOnDuty: e.target.value }))}
                    placeholder="Manager on duty"
                    className="w-full px-3 py-2 border border-neutral-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-neutral-700 mb-1">
                    Others On Duty <span className="text-xs text-neutral-400">(optional)</span>
                  </label>
                  <textarea
                    rows={2}
                    value={formData.othersOnDuty || ''}
                    onChange={e => setFormData(prev => ({ ...prev, othersOnDuty: e.target.value }))}
                    className="w-full px-3 py-2 border border-neutral-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
              </div>
            </div>

            {/* Status */}
            <div>
              <label className="block text-sm font-medium text-neutral-700 mb-1">Status</label>
              <select
                value={formData.status || 'Open'}
                onChange={e => setFormData(prev => ({ ...prev, status: e.target.value }))}
                className="w-full px-3 py-2 border border-neutral-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="Open">Open</option>
                <option value="Closed">Closed</option>
              </select>
            </div>

            {/* Injured persons */}
            <div className="border border-neutral-200 rounded-lg p-4 space-y-3">
              <div className="flex items-center justify-between">
                <h3 className="text-sm font-semibold text-neutral-800 flex items-center gap-2">
                  <Users className="h-4 w-4" />
                  Injured Persons
                </h3>
                <button
                  type="button"
                  onClick={addInjuredPerson}
                  className="text-sm text-blue-600 hover:text-blue-800"
                >
                  + Add injured person
                </button>
              </div>
              {formData.injuredPersons.map((person, index) => (
                <div key={index} className="grid grid-cols-1 md:grid-cols-4 gap-3 items-end">
                  <div>
                    <label className="block text-xs font-medium text-neutral-700 mb-1">Name *</label>
                    <input
                      type="text"
                      value={person.name}
                      onChange={e => updateInjuredPerson(index, 'name', e.target.value)}
                      className="w-full px-2 py-1.5 border border-neutral-300 rounded-md text-sm"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-neutral-700 mb-1">Address *</label>
                    <input
                      type="text"
                      value={person.address || ''}
                      onChange={e => updateInjuredPerson(index, 'address', e.target.value)}
                      className="w-full px-2 py-1.5 border border-neutral-300 rounded-md text-sm"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-neutral-700 mb-1">Phone *</label>
                    <input
                      type="text"
                      value={person.phone || ''}
                      onChange={e => updateInjuredPerson(index, 'phone', e.target.value)}
                      className="w-full px-2 py-1.5 border border-neutral-300 rounded-md text-sm"
                    />
                  </div>
                  <div className="flex gap-2 items-end">
                    <div className="flex-1">
                      <label className="block text-xs font-medium text-neutral-700 mb-1">Birth Date *</label>
                      <input
                        type="date"
                        value={person.birthDate || ''}
                        onChange={e => updateInjuredPerson(index, 'birthDate', e.target.value)}
                        className="w-full px-2 py-1.5 border border-neutral-300 rounded-md text-sm"
                      />
                    </div>
                    <button
                      type="button"
                      onClick={() => removeInjuredPerson(index)}
                      className="text-red-600 hover:text-red-800 text-xs px-2 py-1"
                    >
                      Remove
                    </button>
                  </div>
                </div>
              ))}
            </div>

            {/* Injury details */}
            <div className="border border-neutral-200 rounded-lg p-4 space-y-3">
              <div className="flex items-center justify-between">
                <h3 className="text-sm font-semibold text-neutral-800 flex items-center gap-2">
                  <AlertTriangle className="h-4 w-4" />
                  Injury Details
                </h3>
                <button
                  type="button"
                  onClick={addInjuryDetail}
                  className="text-sm text-blue-600 hover:text-blue-800"
                >
                  + Add injury detail
                </button>
              </div>
              {formData.injuryDetails.map((detail, index) => (
                <div key={index} className="grid grid-cols-1 md:grid-cols-4 gap-3 items-end">
                  <div>
                    <label className="block text-xs font-medium text-neutral-700 mb-1">Type *</label>
                    <input
                      type="text"
                      value={detail.type}
                      onChange={e => updateInjuryDetail(index, 'type', e.target.value)}
                      className="w-full px-2 py-1.5 border border-neutral-300 rounded-md text-sm"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-neutral-700 mb-1">First Aid *</label>
                    <input
                      type="text"
                      value={detail.firstAid || ''}
                      onChange={e => updateInjuryDetail(index, 'firstAid', e.target.value)}
                      className="w-full px-2 py-1.5 border border-neutral-300 rounded-md text-sm"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-neutral-700 mb-1">Hospital Visit By</label>
                    <input
                      type="text"
                      value={detail.hospitalVisitBy || ''}
                      onChange={e => updateInjuryDetail(index, 'hospitalVisitBy', e.target.value)}
                      className="w-full px-2 py-1.5 border border-neutral-300 rounded-md text-sm"
                    />
                  </div>
                  <div className="flex gap-2 items-end">
                    <div className="flex-1">
                      <label className="block text-xs font-medium text-neutral-700 mb-1">Helped By</label>
                      <input
                        type="text"
                        value={detail.helpedBy || ''}
                        onChange={e => updateInjuryDetail(index, 'helpedBy', e.target.value)}
                        className="w-full px-2 py-1.5 border border-neutral-300 rounded-md text-sm"
                      />
                    </div>
                    <button
                      type="button"
                      onClick={() => removeInjuryDetail(index)}
                      className="text-red-600 hover:text-red-800 text-xs px-2 py-1"
                    >
                      Remove
                    </button>
                  </div>
                </div>
              ))}
            </div>

            {/* Garda involved */}
            <div className="border border-neutral-200 rounded-lg p-4 space-y-3">
              <div className="flex items-center justify-between">
                <h3 className="text-sm font-semibold text-neutral-800 flex items-center gap-2">
                  Garda Involved
                </h3>
                <button
                  type="button"
                  onClick={addGarda}
                  className="text-sm text-blue-600 hover:text-blue-800"
                >
                  + Add Garda
                </button>
              </div>
              {formData.gardaInvolved.map((garda, index) => (
                <div key={index} className="grid grid-cols-1 md:grid-cols-3 gap-3 items-end">
                  <div>
                    <label className="block text-xs font-medium text-neutral-700 mb-1">Name</label>
                    <input
                      type="text"
                      value={garda.name}
                      onChange={e => updateGarda(index, 'name', e.target.value)}
                      className="w-full px-2 py-1.5 border border-neutral-300 rounded-md text-sm"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-neutral-700 mb-1">Garda ID</label>
                    <input
                      type="text"
                      value={garda.gardaId}
                      onChange={e => updateGarda(index, 'gardaId', e.target.value)}
                      className="w-full px-2 py-1.5 border border-neutral-300 rounded-md text-sm"
                    />
                  </div>
                  <div className="flex gap-2 items-end">
                    <div className="flex-1">
                      <label className="block text-xs font-medium text-neutral-700 mb-1">Pulse ID</label>
                      <input
                        type="text"
                        value={garda.pulseId}
                        onChange={e => updateGarda(index, 'pulseId', e.target.value)}
                        className="w-full px-2 py-1.5 border border-neutral-300 rounded-md text-sm"
                      />
                    </div>
                    <button
                      type="button"
                      onClick={() => removeGarda(index)}
                      className="text-red-600 hover:text-red-800 text-xs px-2 py-1"
                    >
                      Remove
                    </button>
                  </div>
                </div>
              ))}
            </div>


              <div className="flex justify-end gap-3 pt-4">
                <button
                  type="button"
                  onClick={() => {
                    setShowForm(false);
                    resetForm();
                  }}
                  className="px-4 py-2 border border-neutral-300 rounded-md text-neutral-700 hover:bg-neutral-50"
                  disabled={saving}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={saving}
                  className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {saving ? (editingIncident ? 'Saving...' : 'Creating...') : editingIncident ? 'Save Changes' : 'Create Incident'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default IncidentAdmin;


